import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import { pc } from "../lib/api";
import { useT } from "../lib/i18n";

export const AiChip = ({ text = "AI suggests · You decide" }) => {
  const t = useT();
  return (
  <span data-testid="ai-chip" className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-extrabold" style={{ background: "#DDF7F1", color: "#0F9F87" }}>
    ✨ {t(text)}
  </span>
  );
};

export const Avatar = ({ m, size = 36 }) => {
  const c = pc(m?.color);
  return (
    <span className="inline-flex items-center justify-center rounded-full font-display font-bold text-white shrink-0"
      style={{ width: size, height: size, background: m?.type === "child" ? "#C9C3DB" : c.fill, fontSize: size * 0.45 }}>
      {(m?.name || "?")[0]}
    </span>
  );
};

export const PersonChip = ({ m, onClick, testid }) => {
  if (!m) return null;
  const c = pc(m.color);
  return (
    <button type="button" data-testid={testid || "person-chip"} onClick={onClick} disabled={!onClick}
      className="rounded-full px-2.5 py-1 text-xs font-extrabold whitespace-nowrap" style={{ background: c.tint, color: c.text }}>
      {m.name}{onClick ? " ▾" : ""}
    </button>
  );
};

export const LINES = {
  voice: ["Listening carefully…", "Understanding Marathi, Hindi and English…", "Finding who's free…", "Writing a kind message…"],
  scan: ["Reading the handwriting…", "Counting milk days…", "Checking every line…", "Finding who's free…"],
  type: ["Reading your thoughts…", "Spotting the tasks…", "Finding who's free…", "Writing a kind message…"],
  meals: ["Peeking into your fridge…", "Thinking of home-style dishes…", "Checking what's missing…"],
  setup: ["Listening carefully…", "Filling in your home…", "Making your home plan…"],
  plan: ["Making your home plan…", "Finding who's free…", "Picking kind reasons…"],
  checkin: ["Looking at your open tasks…", "Finding who's free…", "Lightening your evening…"],
  story: ["Counting this week's tasks…", "Writing your story…"],
  send: ["Sharing the load…", "Writing a kind message…"],
};

export function AiLoader({ kind = "type" }) {
  const lines = LINES[kind] || LINES.type;
  const [i, setI] = useState(0);
  const t = useT();
  useEffect(() => { const h = setInterval(() => setI((x) => (x + 1) % lines.length), 1600); return () => clearInterval(h); }, [lines.length]);
  return (
    <div data-testid="ai-loader" className="ul-card p-6 flex flex-col items-center gap-3 text-center">
      <div className="text-4xl animate-bounce" style={{ animationDuration: "2s" }}>✨</div>
      <motion.p key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="ul-shimmer font-display text-xl font-bold">{t(lines[i])}</motion.p>
    </div>
  );
}

export const Empty = ({ emoji = "🌿", text }) => {
  const t = useT();
  return (
    <div data-testid="empty-state" className="flex flex-col items-center text-center py-10 px-6 gap-2">
      <div className="text-5xl">{emoji}</div>
      <p className="text-[#6B6680] font-semibold">{t(text)}</p>
    </div>
  );
};

export const ErrorCard = ({ text, onRetry }) => {
  const t = useT();
  return (
    <div data-testid="error-card" className="ul-card p-5 text-center" style={{ background: "#FFF6EC" }}>
      <p className="font-semibold mb-3">🙈 {t(text || "Something went wrong.")}</p>
      {onRetry && <button data-testid="try-again-btn" className="ul-btn ul-primary" onClick={onRetry}>{t("Try again")}</button>}
    </div>
  );
};

export const Header = ({ kicker, title, right }) => {
  const t = useT();
  return (
    <div className="flex items-start justify-between gap-3 pt-5 pb-3">
      <div>
        {kicker && <p className="text-sm text-[#6B6680] font-semibold">{t(kicker)}</p>}
        <h1 className="font-display text-[28px] leading-tight font-extrabold">{t(title)}</h1>
      </div>
      {right}
    </div>
  );
};

export const Chips = ({ options, value = [], onChange, multi = true, testid = "chip", ai }) => {
  const t = useT();
  return (
  <div className={`flex flex-wrap gap-2 ${ai ? "ul-ai rounded-xl p-2" : ""}`}>
    {options.map((o) => {
      const [v, label] = Array.isArray(o) ? o : [o, o];
      const on = multi ? value.includes(v) : value === v;
      return (
        <button key={v} type="button" data-on={on} data-testid={`${testid}-${String(v).replace(/\W+/g, "-").toLowerCase()}`} className="ul-chip"
          onClick={() => onChange(multi ? (on ? value.filter((x) => x !== v) : [...value, v]) : on ? null : v)}>
          {t(label)}
        </button>
      );
    })}
  </div>
  );
};

export const MoneyNote = () => {
  const t = useT();
  return (
    <p className="text-xs text-[#6B6680] mt-2" data-testid="money-note">
      {t("AI suggests. You decide. Please check before paying. UnloadIt never pays.")}{" "}
      <a href="/legal/money" className="underline text-[#6A5AE0]">{t("Why?")}</a>
    </p>
  );
};

export const celebrate = () => {
  confetti({ particleCount: 70, spread: 65, startVelocity: 32, origin: { y: 0.75 }, colors: ["#6A5AE0", "#FF6FA3", "#2EC4B6", "#F4B400"], disableForReducedMotion: true });
};

export const Sheet = ({ open, onClose, children, testid = "sheet" }) => {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-center items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-[#2A2540]/30 backdrop-blur-[2px]" />
      <motion.div data-testid={testid} initial={{ y: 300 }} animate={{ y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }}
        className="relative w-full max-w-[480px] bg-white rounded-t-[24px] p-5 pb-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-1.5 bg-[#E4DEFA] rounded-full mx-auto mb-4" />
        {children}
      </motion.div>
    </div>,
    document.body
  );
};
